'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { StoreBadgeLinks } from '@/shared/ui/StoreBadgeLinks';

/**
 * Web stand-in for a purchase CTA: web has no purchases (existing pattern —
 * payments run through the mobile apps), so every pack / Builder Pass
 * paywall ends in this block instead of a buy button. One short
 * localized direction line + the store badge pair; the offer copy above it
 * stays fully server-driven.
 */
export function DirectToAppPurchase({
  className,
  onStoreTap,
  verbLabel,
  calm = false,
}: {
  className?: string;
  /** Funnel hook — fired when the user taps a store badge (the web "purchase
   *  started" analog). The owning paywall passes the right event/params. */
  onStoreTap?: () => void;
  /**
   * Verb CTA lead (e.g. "Get the Facade Pack" / "Get Builder Pass"). When set,
   * a hug-content teal capsule leading the store badges gives the storefront
   * product cards a verb CTA at parity with the Apple cards — web is read-only
   * for payments, so the capsule anchors the store-badge handoff rather than a
   * checkout. The price stays the top-right badge, never on this button.
   */
  verbLabel?: string;
  /**
   * Calmer, secondary (outlined) verb capsule instead of the loud filled teal —
   * used for the "Additional Facade Kit" repeat purchase, where buying a second
   * building's kit is a lower-stakes add-on. Mirrors Apple's `.secondaryCapsule`.
   */
  calm?: boolean;
}) {
  const t = useTranslations('packs');
  const appStoreUrl = process.env.NEXT_PUBLIC_APPSTORE_URL || '#';

  return (
    <div className={cn('flex w-full flex-col items-center gap-3', className)}>
      {verbLabel && (
        <a
          href={appStoreUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onStoreTap}
          className={cn(
            // `min-h-11` + vertical padding (not a fixed `h-11`) and centered,
            // wrapping text so a long building name + long currency can break to
            // a second line instead of clipping — the label must never truncate.
            'inline-flex min-h-11 items-center justify-center whitespace-normal text-center rounded-full px-6 py-2 text-list-item-value font-semibold transition-opacity hover:opacity-90',
            calm
              ? 'border border-primary text-primary hover:bg-primary/5'
              : 'bg-primary text-primary-foreground'
          )}
        >
          {verbLabel}
        </a>
      )}
      <p className="text-card-meta text-muted-foreground text-center">
        {t('getInAppNote')}
      </p>
      <StoreBadgeLinks className="justify-center" onBadgeClick={onStoreTap} />
    </div>
  );
}
